import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Mail, MapPin, Phone } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Notice } from "@/components/Badges";
import { useAuth } from "@/context/AuthContext";

const validate = (f) => {
  const e = {};
  if (!f.name.trim()) e.name = "Please enter your name.";
  if (!f.email.trim()) e.email = "Please enter your email address.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = "Please enter a valid email address, like name@company.co.uk.";
  if (f.message.trim().length < 10) e.message = "Please write a message of at least 10 characters.";
  return e;
};

const Details = () => {
  const { settings } = useAuth();
  const rows = [
    [Mail, settings.support_email || "[Contact email]", settings.support_email && `mailto:${settings.support_email}`, "contact-detail-email"],
    [Phone, settings.support_phone || "[Phone number]", settings.support_phone && `tel:${settings.support_phone.replace(/\s/g, "")}`, "contact-detail-phone"],
    [MapPin, settings.support_address || "[Business address]", null, "contact-detail-address"],
  ];
  return (
    <ul className="mt-8 grid gap-3 sm:grid-cols-3">
      {rows.map(([Icon, text, href, id]) => (
        <li key={id} className="card flex items-start gap-3 p-4 text-sm" data-testid={id}>
          <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#1F8A8B]" aria-hidden />
          {href ? <a href={href} className="break-all font-medium text-[#1E2A33] hover:text-[#1F8A8B]">{text}</a> : <span className="text-slate-700">{text}</span>}
        </li>
      ))}
    </ul>
  );
};

const Err = ({ id, msg }) => (msg ? <p id={id} data-testid={id} className="mt-1.5 text-xs font-medium text-red-600">{msg}</p> : null);

export default function Contact() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", email: "", subject: "", message: "", type: params.get("type") || "contact" });
  const [errors, setErrors] = useState({});
  const [serverErr, setServerErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const v = validate(f);
    setErrors(v);
    setServerErr("");
    if (Object.keys(v).length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/public/contact", f);
      navigate("/thank-you", { state: { reference: data.reference } });
    } catch (err) {
      setServerErr(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); if (errors[k]) setErrors({ ...errors, [k]: undefined }); };
  const cls = (k) => `input ${errors[k] ? "border-red-400 focus:border-red-500" : ""}`;
  const aria = (k) => ({ "aria-invalid": Boolean(errors[k]), "aria-describedby": errors[k] ? `contact-${k}-error` : undefined });
  return (
    <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
      <p className="caption text-[#0D9488]">Contact</p>
      <h1 className="mt-3 text-4xl font-bold text-[#0B192C] sm:text-5xl">Get in touch</h1>
      <p className="mt-4 text-slate-600">Questions, support or an expert-review request. Please do not include confidential invention details in this form.</p>
      <Details />
      <form noValidate onSubmit={submit} className="card mt-8 space-y-5 p-6 sm:p-8" data-testid="contact-form">
        {serverErr && <Notice tone="warn" testid="contact-server-error">{serverErr}</Notice>}
        <div className="grid gap-5 sm:grid-cols-2">
          <div><label className="label" htmlFor="c-name">Name</label><input id="c-name" data-testid="contact-name-input" className={cls("name")} value={f.name} onChange={set("name")} {...aria("name")} /><Err id="contact-name-error" msg={errors.name} /></div>
          <div><label className="label" htmlFor="c-email">Email</label><input id="c-email" type="email" data-testid="contact-email-input" className={cls("email")} value={f.email} onChange={set("email")} {...aria("email")} /><Err id="contact-email-error" msg={errors.email} /></div>
        </div>
        <div><label className="label" htmlFor="c-type">Enquiry type</label>
          <select id="c-type" data-testid="contact-type-select" className="input" value={f.type} onChange={set("type")}>
            <option value="contact">General enquiry</option><option value="support">Platform support</option><option value="expert_review">Expert review request</option>
          </select>
        </div>
        <div><label className="label" htmlFor="c-sub">Subject (optional)</label><input id="c-sub" data-testid="contact-subject-input" className="input" value={f.subject} onChange={set("subject")} /></div>
        <div><label className="label" htmlFor="c-msg">Message</label><textarea id="c-msg" rows={6} data-testid="contact-message-input" className={cls("message")} value={f.message} onChange={set("message")} {...aria("message")} /><Err id="contact-message-error" msg={errors.message} /></div>
        <button type="submit" disabled={busy} data-testid="contact-submit-button" className="btn-teal disabled:opacity-60">{busy ? <><Loader2 className="h-4 w-4 animate-spin" />Sending…</> : "Send message"}</button>
      </form>
    </div>
  );
}
